import * as React from "react";

/**
 * Why is this ever used?
 * 
 * Well, ios safari can change the actual height of the window WITHOUT updating the css height.
 * 
 * see: https://medium.com/@susiekim9/how-to-compensate-for-the-ios-viewport-unit-bug-46e78d54af0d
 * 
 * Benjamin Poulain, an engineer at Apple replied to Hoizey . He stated — in true programmer fashion — that it wasn’t a bug, it’s a feature.
 * This is completely intentional. It took quite a bit of work on our part to achieve this effect. :)
 * The base problem is this: the visible area changes dynamically as you scroll. If we update the CSS viewport height accordingly, we need to update the layout during the scroll. Not only that looks like shit, but doing that at 60 FPS is practically impossible in most pages (60 FPS is the baseline framerate on iOS).
 * It is hard to show you the "looks like shit" part, but imagine as you scroll, the contents moves and what you want on screen is continuously shifting.
 * Dynamically updating the height was not working, we had a few choices: drop viewport units on iOS, match the document size like before iOS 8, use the small view size, use the large view size.
 * From the data we had, using the larger view size was the best compromise. Most website using viewport units were looking great most of the time.
 */


let warned = false;

export const Div100vh = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ style, ...other }, ref) => {
  const height = use100vh();

  // TODO: warn only in development
  if (!warned && style?.height) {
    warned = true;
    console.warn(
      "<Div100vh /> overrides the height property of the style prop"
    );
  }
  const styleWithRealHeight = {
    ...style,
    height: height ? `${height}px` : "100vh",
  };
  return <div ref={ref} style={styleWithRealHeight} {...other} />;
});

Div100vh.displayName = "Div100vh";

export function use100vh(): number | null {
  const [height, setHeight] = React.useState<number | null>(measureHeight);

  const wasRenderedOnClientAtLeastOnce = useWasRenderedOnClientAtLeastOnce();

  React.useEffect(() => {
    if (!wasRenderedOnClientAtLeastOnce) return;

    function setMeasuredHeight() {
      const measuredHeight = measureHeight();
      setHeight(measuredHeight);
    }

    window.addEventListener("resize", setMeasuredHeight);
    return () => window.removeEventListener("resize", setMeasuredHeight);
  }, [wasRenderedOnClientAtLeastOnce]);
  return wasRenderedOnClientAtLeastOnce ? height : null;
}

function measureHeight(): number | null {
  if (!isClient()) return null;
  return document.documentElement?.clientHeight || window.innerHeight;
}

// Once we ended up on the client, the first render must look the same as on
// the server so hydration happens without problems. _Then_ we immediately
// schedule a subsequent update and return the height measured on the client.
// It's not needed for CSR-only apps, but is critical for SSR.
function useWasRenderedOnClientAtLeastOnce() {
  const [wasRenderedOnClientAtLeastOnce, setWasRenderedOnClientAtLeastOnce] =
    React.useState(false);

  React.useEffect(() => {
    if (isClient()) {
      setWasRenderedOnClientAtLeastOnce(true);
    }
  }, []);
  return wasRenderedOnClientAtLeastOnce;
}

function isClient() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}
